// Tour step definitions per role
// Steps with `selector` spotlight that element. Steps with `fallback: true` show as centered cards
// when the selector target doesn't exist on the page (e.g., no projects yet).

export const CLIENT_STEPS = [
  {
    selector: '[data-testid="sidebar-nav"]',
    title: 'Welcome to Uncut Gems',
    desc: 'This is your editing portal. Everything you need — from placing orders to reviewing edits — lives here in the sidebar.',
    position: 'right',
  },
  {
    selector: '[data-testid="nav-style-profile"]',
    title: 'Complete Your Style Profile',
    desc: 'Before placing your first order, fill out your Style Profile. This tells your editor your preferences — color grading, music taste, pacing — so they can match your vision from the very first cut.',
    position: 'right',
  },
  {
    selector: '[data-testid="new-project-btn"]',
    title: 'Place a New Order',
    desc: 'When you\'re ready, click here to start a new project. You\'ll choose your products, provide your footage links, and submit your order.',
    position: 'left',
  },
  {
    selector: '[data-testid="search-projects"]',
    title: 'Your Project Board',
    desc: 'All your projects appear here, grouped by status. You\'ll see them move from "Order Submitted" through "Editing in Progress" to "First Draft Complete" as your editor works.',
    position: 'bottom',
  },
  {
    title: 'Estimated Timeline',
    desc: 'Once your project is in progress, you\'ll see an estimated delivery date in the ETA column. This is our best estimate based on your editor\'s current workload — not a guarantee, but we do our best to hit it.',
    fallback: true,
  },
  {
    title: 'Reviewing Your Edits',
    desc: 'When your editor delivers a draft, click on the project to open the detail panel on the right. You\'ll find a Review tab where you can watch your video or browse your photos, leave timestamped feedback, flag specific items, and request revisions or approve the final result.',
    fallback: true,
  },
  {
    selector: '[data-testid="nav-chats"]',
    title: 'Chat With Your Team',
    desc: 'Have a question? Use the chat to message your project manager directly. You\'ll also get notifications here when your draft is ready for review.',
    position: 'right',
  },
];

export const EDITOR_STEPS = [
  {
    selector: '[data-testid="sidebar-nav"]',
    title: 'Your Editor Dashboard',
    desc: 'Welcome! Projects assigned to you appear on the main board, grouped by status. Focus on "Assigned to Editor" and "Editing in Progress" first.',
    position: 'right',
  },
  {
    title: 'Updating Project Status',
    desc: 'As you work, update the status by clicking the status pill on any project row. Moving to "Editing in Progress" lets the team know you\'ve started. Submitting "First Draft Complete" sends the draft to your project manager for review before the client sees it.',
    fallback: true,
  },
  {
    title: 'Your Due Dates',
    desc: 'The Due Date column shows when the first draft is expected. Submitting late automatically pushes your other project deadlines forward, so staying on track keeps everything flowing smoothly.',
    fallback: true,
  },
  {
    title: 'Uploading Deliverables',
    desc: 'Click any project to open the detail panel, then go to the Review tab. For video projects, upload your draft here. For photo projects, drag and drop the full edited gallery. Important: when uploading revised photos, use the exact same filenames so the system can track which ones were corrected.',
    fallback: true,
  },
  {
    title: 'Accessing Client Footage',
    desc: 'In the Files tab of any project, press Download footage: Footage Pull downloads it, and the project prepared for you comes with it. Footage Pull is the only way to get footage.',
    fallback: true,
  },
  {
    selector: '[data-testid="nav-editor-guide"]',
    title: 'Editor Guide',
    desc: 'The Editor Guide in the sidebar has everything you need: our workflow steps, editing standards, Musicbed login, the editing course, and details on annual bonuses you can earn.',
    position: 'right',
  },
  {
    selector: '[data-testid="nav-notifications"]',
    title: 'Notifications & Internal Chat',
    desc: 'Check notifications for new assignments and revision requests. Use the Internal Editor Chat on any project to communicate privately with your PM — clients can\'t see these messages.',
    position: 'right',
  },
];

export const ADMIN_STEPS = [
  {
    selector: '[data-testid="dashboard-stats"]',
    title: 'Dashboard Overview',
    desc: 'Your at-a-glance metrics: unread client chats, overdue projects, unassigned editors, new orders, and editor chats. These numbers update in real time.',
    position: 'bottom',
  },
  {
    selector: '[data-testid="search-projects"]',
    title: 'Project Board',
    desc: 'All projects grouped by status. Click any column header to sort within each group. Use the search bar to find projects by name, client, editor, or product.',
    position: 'bottom',
  },
  {
    title: 'Assigning Editors & PMs',
    desc: 'Click the editor circle on any project row to assign or change the editor. The system recommends editors based on software match, workload, and ratings. Click the PM circle to assign a project manager.',
    fallback: true,
  },
  {
    title: 'Readiness Indicators',
    desc: 'The Media, Music, and Notes columns show green checks or red X\'s at a glance. Green = ready, red = missing. This helps you quickly identify which projects are blocked and need attention.',
    fallback: true,
  },
  {
    title: 'Project Details & Review',
    desc: 'Click any project to open the detail panel on the right. From there you can review files, update statuses, manage versions, read client and editor chat, and track the full activity history.',
    fallback: true,
  },
  {
    selector: '[data-testid="nav-admin-payments"]',
    title: 'Payments',
    desc: 'Track editor payments, generate payment records, and manage billing. Each editor\'s pay rates are configurable per product on the Users page.',
    position: 'right',
  },
  {
    selector: '[data-testid="nav-admin-analytics"]',
    title: 'Analytics',
    desc: 'Revenue trends, editor performance, turnaround times, and client metrics. All columns are sortable so you can quickly find top performers or spot bottlenecks.',
    position: 'right',
  },
  {
    selector: '[data-testid="nav-admin-settings"]',
    title: 'Settings',
    desc: 'Configure global settings like the NAS link, manage products and pricing, and control user accounts from the sidebar.',
    position: 'right',
  },
];

// Staff/PM uses the same steps as admin but without settings
export const STAFF_STEPS = ADMIN_STEPS.filter(s => s.selector !== '[data-testid="nav-settings"]');
